import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { contractService } from '../services/contract.service';

// Async thunks
export const fetchContracts = createAsyncThunk(
  'contract/fetchContracts',
  async (_, { rejectWithValue }) => {
    try {
      return await contractService.getContracts();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des contrats');
    }
  }
);

export const fetchActiveContracts = createAsyncThunk(
  'contract/fetchActiveContracts',
  async (_, { rejectWithValue }) => {
    try {
      return await contractService.getActiveContracts();
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des contrats actifs');
    }
  }
);

export const fetchContractById = createAsyncThunk(
  'contract/fetchContractById',
  async (id, { rejectWithValue }) => {
    try {
      return await contractService.getContractById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du contrat');
    }
  }
);

export const fetchContractByNumber = createAsyncThunk(
  'contract/fetchContractByNumber',
  async (contractNumber, { rejectWithValue }) => {
    try {
      return await contractService.getContractByNumber(contractNumber);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération du contrat');
    }
  }
);

export const addContract = createAsyncThunk(
  'contract/addContract',
  async (contract, { rejectWithValue }) => {
    try {
      const result = await contractService.createContract(contract);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la création du contrat');
    }
  }
);

export const editContract = createAsyncThunk(
  'contract/editContract',
  async ({ id, contract }, { rejectWithValue }) => {
    try {
      await contractService.updateContract(id, contract);
      return { id, ...contract };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour du contrat');
    }
  }
);

export const removeContract = createAsyncThunk(
  'contract/removeContract',
  async (id, { rejectWithValue }) => {
    try {
      await contractService.deleteContract(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la suppression du contrat');
    }
  }
);

// Initial state
const initialState = {
  contracts: [],
  totalCount: 0,
  selectedContract: null,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

// Slice
const contractSlice = createSlice({
  name: 'contract',
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedContract: (state, action) => {
      state.selectedContract = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contracts
      .addCase(fetchContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = action.payload.items || action.payload;
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active contracts
      .addCase(fetchActiveContracts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contracts = action.payload.items || action.payload;
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchActiveContracts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch contract by number
      .addCase(fetchContractByNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContractByNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedContract = action.payload;
      })
      .addCase(fetchContractByNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add contract
      .addCase(addContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContract.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.contracts)) {
          state.contracts.push(action.payload);
        } else {
          state.contracts = [action.payload];
        }
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(addContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit contract
      .addCase(editContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editContract.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.contracts)) {
          const index = state.contracts.findIndex(
            contract => contract.contractId === action.payload.id
          );
          if (index !== -1) {
            state.contracts[index] = {
              ...state.contracts[index],
              ...action.payload,
              contractId: state.contracts[index].contractId,
            };
          }
        }
        state.showModal = false;
      })
      .addCase(editContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove contract
      .addCase(removeContract.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeContract.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(state.contracts)) {
          state.contracts = state.contracts.filter(contract => contract.contractId !== action.payload);
          state.totalCount = Math.max(0, state.totalCount - 1);
        }
        state.showDeleteModal = false;
      })
      .addCase(removeContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedContract
} = contractSlice.actions;

export default contractSlice.reducer;